import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { AppShell } from '../../app/AppShell'
import { NoticeList } from '../../app/NoticeList'
import { ApiError } from '../../api/problem'
import { useCampaignSheet, useDeleteCampaign } from '../../domains/campaigns/queries'
import { useCampaignEditing } from '../../domains/campaigns/useCampaignEditing'
import { CityCard } from '../../domains/campaigns/sections/CityCard'
import { NotesCard } from '../../domains/campaigns/sections/NotesCard'
import { PlayCard } from '../../domains/campaigns/sections/PlayCard'
import { PlayersCard } from '../../domains/campaigns/sections/PlayersCard'
import { RosterCard } from '../../domains/campaigns/sections/RosterCard'
import { Banner, Button, Card, Input } from '../../design/components'
import styles from './campaigns.module.css'

/**
 * A campaign, driven by the same section engine as the character sheet — the
 * implementation plan's promise that Stage 5's hook is reused rather than
 * re-implemented.
 *
 * Card order runs outward from the table: the city (where the story happens), the
 * roster (who is in it), the players (who is at the table), then the reader's own
 * private notes.
 */
export function CampaignDetailPage() {
  const params = useParams<{ id: string }>()
  const campaignId = Number(params.id)

  const query = useCampaignSheet(campaignId)
  const core = query.data?.sections.core

  // Before the early returns — hooks cannot be conditional. All three flags are the
  // server's word: who runs this campaign, who is in it, and whether this caller may
  // still attach a character.
  const editing = useCampaignEditing(campaignId, {
    isGameMaster: query.data?.isGameMaster === true,
    isMember: query.data !== undefined,
    canAddCharacter: core?.canAddCharacter === true,
  })

  if (Number.isNaN(campaignId)) {
    return <CampaignMessage message="That campaign id is not a number." />
  }

  if (query.isPending) {
    return <CampaignMessage message="Loading…" subtitle="FETCHING CAMPAIGN" />
  }

  if (query.isError) {
    // 404 covers "exists, but not yours" — the API resolves campaign ids from an
    // access-checked root, so a stranger cannot probe them. Either way it is an
    // empty state, not an error boundary.
    const notFound = query.error instanceof ApiError && query.error.kind === 'not-found'
    const forbidden = query.error instanceof ApiError && query.error.kind === 'forbidden'

    return (
      <CampaignMessage
        message={
          notFound || forbidden
            ? 'No such campaign, or it is not one of yours.'
            : 'Could not load this campaign.'
        }
        tone={notFound || forbidden ? 'info' : 'error'}
      />
    )
  }

  const sheet = query.data

  return (
    <AppShell
      title={core?.name ?? 'Campaign'}
      subtitle={subtitleFor(sheet.isGameMaster, core?.participantCount, core?.characterCount)}
    >
      <NoticeList notices={editing.notices} failure={editing.failure} onDismiss={editing.dismiss} />

      <CityCard
        city={sheet.sections.city}
        editing={editing}
        {...(core?.cityId != null && { linkTo: `/campaigns/${campaignId}/city` })}
      />

      <PlayCard campaignId={campaignId} core={core} isGameMaster={sheet.isGameMaster} />

      <RosterCard characters={sheet.sections.characters} editing={editing} />
      <PlayersCard players={sheet.sections.players} editing={editing} campaignId={campaignId} />
      <NotesCard notes={sheet.sections.notes} editing={editing} />

      {sheet.isGameMaster && (
        <GameMasterCard campaignId={campaignId} name={core?.name ?? ''} editing={editing} />
      )}

      <Link to="/campaigns" className={styles.backLink}>
        <Button variant="secondary" fullWidth>
          All campaigns
        </Button>
      </Link>
    </AppShell>
  )
}

/**
 * The two writes that are about the campaign itself rather than anything in it.
 *
 * Deleting is kept behind a confirmation and phrased in terms of what actually goes:
 * the city, its factions and places, membership, roster links and notes all cascade —
 * the characters themselves do not.
 */
function GameMasterCard({
  campaignId,
  name,
  editing,
}: {
  campaignId: number
  name: string
  editing: ReturnType<typeof useCampaignEditing>
}) {
  const navigate = useNavigate()
  const remove = useDeleteCampaign()

  const [renaming, setRenaming] = useState(false)
  const [nextName, setNextName] = useState(name)
  const [confirming, setConfirming] = useState(false)

  return (
    <Card variant="stunts" label="Game Master">
      {renaming ? (
        <form
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault()
            editing.rename.mutate({ name: nextName }, { onSuccess: () => setRenaming(false) })
          }}
        >
          <Input
            label="Campaign name"
            value={nextName}
            autoFocus
            onChange={(e) => setNextName(e.target.value)}
          />
          <div className={styles.formActions}>
            <Button
              variant="secondary"
              onClick={() => {
                setNextName(name)
                setRenaming(false)
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={editing.rename.isPending || nextName.trim() === ''}>
              {editing.rename.isPending ? 'Saving…' : 'Rename'}
            </Button>
          </div>
        </form>
      ) : (
        <div className={styles.formActions}>
          <Button variant="secondary" onClick={() => setRenaming(true)}>
            Rename campaign
          </Button>
        </div>
      )}

      <div className={styles.danger}>
        {confirming ? (
          <>
            <p className={styles.note}>
              Deleting removes the city, its factions and places, the player list, the roster links
              and every note. The characters themselves are untouched.
            </p>
            <div className={styles.formActions}>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                Keep it
              </Button>
              <Button
                variant="danger"
                disabled={remove.isPending}
                onClick={() => {
                  remove.mutate(campaignId, { onSuccess: () => void navigate('/campaigns') })
                }}
              >
                {remove.isPending ? 'Deleting…' : 'Delete campaign'}
              </Button>
            </div>
          </>
        ) : (
          <Button variant="danger" fullWidth onClick={() => setConfirming(true)}>
            Delete campaign
          </Button>
        )}
      </div>
    </Card>
  )
}

function CampaignMessage({
  message,
  subtitle,
  tone = 'info',
}: {
  message: string
  subtitle?: string
  tone?: 'info' | 'error'
}) {
  return (
    <AppShell title="Campaign" {...(subtitle !== undefined && { subtitle })}>
      <Card>
        <Banner tone={tone}>{message}</Banner>
      </Card>
      <Link to="/campaigns" className={styles.backLink}>
        <Button variant="secondary" fullWidth>
          All campaigns
        </Button>
      </Link>
    </AppShell>
  )
}

/** e.g. "GAME MASTER · 4 AT THE TABLE · 3 CHARACTERS". */
function subtitleFor(
  isGameMaster: boolean,
  participants: number | undefined,
  characters: number | undefined,
): string {
  const parts = [isGameMaster ? 'Game Master' : 'Player']

  if (participants !== undefined) {
    parts.push(participants === 1 ? '1 at the table' : `${participants} at the table`)
  }
  if (characters !== undefined) {
    parts.push(characters === 1 ? '1 character' : `${characters} characters`)
  }

  return parts.join(' · ')
}
